import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { ArrowLeft } from "lucide-react";
import { Link, useLocation } from "react-router";

type AccountPageHeaderProps = {
  title: string;
  description?: ReactNode;
  icon?: LucideIcon;
  backTo?: string;
  onBack?: () => void;
  backLabel?: string;
  actions?: ReactNode;
  titleAdornment?: ReactNode;
};

export function AccountPageHeader({
  title,
  description,
  icon: Icon,
  backTo,
  onBack,
  backLabel = "Back",
  actions,
  titleAdornment,
}: AccountPageHeaderProps) {
  const { pathname } = useLocation();
  const normalizedTitle = title.toUpperCase();
  const eyebrow = pathname.startsWith("/account")
    ? "ACCOUNT / PROFILE & EMAIL"
    : pathname === "/organizations"
      ? "ACCOUNT / ORGANIZATIONS"
      : pathname.startsWith("/organizations/")
        ? `ACCOUNT / ORGANIZATIONS / ${normalizedTitle}`
        : pathname === "/subscriptions"
          ? "ACCOUNT / SUBSCRIPTIONS"
          : pathname.startsWith("/subscriptions/")
            ? `ACCOUNT / SUBSCRIPTIONS / ${normalizedTitle}`
            : "ACCOUNT";

  return (
    <header className="account-page-header">
      <p className="account-page-header__eyebrow">{eyebrow}</p>
      {backTo ? (
        <Link className="account-page-header__back" to={backTo}>
          <ArrowLeft aria-hidden="true" />
          {backLabel}
        </Link>
      ) : onBack ? (
        <button className="account-page-header__back" type="button" onClick={onBack}>
          <ArrowLeft aria-hidden="true" />
          {backLabel}
        </button>
      ) : null}

      <div className="account-page-header__row">
        <div className="account-page-header__identity">
          {Icon && (
            <span className="account-page-header__icon" aria-hidden="true">
              <Icon />
            </span>
          )}
          <div className="account-page-header__copy">
            <div className="account-page-header__title-line">
              <h1>{title}</h1>
              {titleAdornment}
            </div>
            {description && <p>{description}</p>}
          </div>
        </div>
        {actions && <div className="account-page-header__actions">{actions}</div>}
      </div>
    </header>
  );
}
